import { AssignmentViewMode, ViewMode } from '@features/custom-metrics/constants';
import { CardGridButtonType } from '@features/custom-metrics/CustomMetricGroups';
import { QuestionsAssignmentTable } from '@features/custom-metrics/QuestionsAssignmentTable';
import { DEFAULT_CUSTOM_METRICS_USAGE } from '@features/custom-metrics/utils';
import CardGrid, { CardGridItem, CardGridItemProps, CardGridViewMode } from '@features/survey-scope/CardGrid';
import { ROUTES } from '@constants/routes';
import { QUESTION } from '@constants/terminology';
import { Breadcrumbs } from '@g17eco/molecules/breadcrumbs';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { MetricGroup } from '@g17eco/types/metricGroup';
import { generateUrlWithQuery } from '@routes/util';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import { getCustomMetricGroups } from './utils';
import { BasicAlert } from '@g17eco/molecules/alert';
import { filterVisibleSubgroups } from '@utils/metricGroup';

interface Props {
  metricGroup: MetricGroup;
  subGroup?: MetricGroup;
  assignmentViewMode?: AssignmentViewMode;
  handleReload: () => Promise<void>;
}

const generateUrl = ({
  metricGroup,
  assignmentViewMode,
  subGroup,
}: {
  metricGroup: MetricGroup;
  subGroup?: Pick<MetricGroup, '_id'>;
  assignmentViewMode: AssignmentViewMode;
}) => {
  return generateUrlWithQuery(
    ROUTES.MATERIALITY_TRACKER_MODULES,
    {
      initiativeId: metricGroup.initiativeId,
      groupId: metricGroup._id,
      viewMode: ViewMode.AssignQuestions,
    },
    { assignmentViewMode, subGroupId: subGroup?._id },
  );
};

const getCardButtons = ({ handleClick }: { handleClick: () => void }) => {
  return [
    {
      type: CardGridButtonType.Edit,
      button: (
        <Button color='secondary' size='xs' onClick={handleClick}>
          <i className='fal fa-list mr-2' />
          View {QUESTION.PLURAL}
        </Button>
      ),
    },
  ];
};

const viewModeOptions = [
  {
    value: AssignmentViewMode.SubModules,
    label: 'Sub-modules',
  },
  {
    value: AssignmentViewMode.Questions,
    label: 'Distinct list',
  },
];

export const MetricGroupAssignment = ({
  metricGroup,
  subGroup,
  assignmentViewMode = AssignmentViewMode.SubModules,
  handleReload,
}: Props) => {
  const history = useHistory();

  const handleViewSubgroup = (selectedSubGroup: MetricGroup) => {
    history.push(generateUrl({ metricGroup, subGroup: selectedSubGroup, assignmentViewMode }));
  };

  if (subGroup) {
    return (
      <>
        <div className='mb-3'>
          <Breadcrumbs
            breadcrumbs={[{ label: subGroup.groupName }]}
            rootLabel={metricGroup.groupName}
            rootUrl={generateUrl({ metricGroup, assignmentViewMode })}
          />
        </div>
        <QuestionsAssignmentTable
          metricGroup={subGroup}
          handleReload={handleReload}
          canEdit={false}
          readOnly={false}
          customMetricsUsage={DEFAULT_CUSTOM_METRICS_USAGE}
        />
      </>
    );
  }

  const subGroupCards = getCustomMetricGroups({
    metricGroups: filterVisibleSubgroups(metricGroup),
    getButtons: (selectedSubGroup) => getCardButtons({ handleClick: () => handleViewSubgroup(selectedSubGroup) }),
  });

  return (
    <>
      <div className='d-flex justify-content-end mb-3'>
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          options={viewModeOptions}
          value={viewModeOptions.find((v) => v.value === assignmentViewMode)}
          onChange={(option) =>
            option ? history.push(generateUrl({ metricGroup, assignmentViewMode: option.value })) : undefined
          }
          isSearchable={false}
        />
      </div>
      {assignmentViewMode === AssignmentViewMode.SubModules ? (
        subGroupCards.length ? (
          <CardGrid viewLayout={CardGridViewMode.gallery}>
            {subGroupCards.map(({ key, ...cardProps }: CardGridItemProps) => (
              <CardGridItem key={key} {...cardProps} />
            ))}
          </CardGrid>
        ) : (
          <BasicAlert type='warning'>There are no sub-modules currently available.</BasicAlert>
        )
      ) : (
        <QuestionsAssignmentTable
          metricGroup={metricGroup}
          handleReload={handleReload}
          canEdit={false}
          readOnly={false}
          customMetricsUsage={DEFAULT_CUSTOM_METRICS_USAGE}
        />
      )}
    </>
  );
};
